import { AutogenLayoutTimecardProcessing } from "./autogen/AutogenLayoutTimecardProcessing";
import {
    BlurEvent, ClickEvent, Component, CrudDecorator, DataSource, DataSourceMode, KeyEvent, Label, Layout,
    PrintableEvent, ResizeEvent, TableRow, TableRowBeforeSaveEvent, TableRowDisplayEvent, Textbox
} from "@mcleod/components";
import { FilterPanel, SecondaryFilterProps } from "@mcleod/dispatch";
import { Currency, DisplayType, DOMUtil, Keys, ModelRow, Navigation } from "@mcleod/core";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { getPayrollControlBoolean } from "@mcleod/general";
import { OfficePayeeDataLookup, SettlementPayeeDataLookup } from "./PayeeData";
import { AssignTimecardHours } from "./AssignTimecardHours";
import { TableFilterChangedEvent } from "@mcleod/components/src/events/TableFilterChangedEvent";

interface TimecardProcessingProps {
    dataSource: DataSource,
    payeeDataLookup: SettlementPayeeDataLookup | OfficePayeeDataLookup
    payeeId?: string
}

const ENTERED_HOURS: SecondaryFilterProps = {
    id: "enteredHours",
    caption: "Entered Hours",
    filter: (row: ModelRow) => {
        return !row.getCurrency("total_pay", new Currency(0)).isZero();
    }
};

const NEEDS_REVIEW: SecondaryFilterProps = {
    id: "needsReview",
    caption: "Needs Review",
    filter: (row: ModelRow) => {
        const hasSomeRates = row.get("regular_rate", 0) > 0 || row.get("overtime_rate", 0) > 0 || row.get("vacation_rate", 0) > 0;
        return row.getCurrency("total_pay", new Currency(0)).isZero() && row.isNull("payee_process_status") && hasSomeRates;
    }
};

const MISSING_RATES: SecondaryFilterProps = {
    id: "missingRates",
    caption: "Missing Rates",
    filter: (row: ModelRow) => {
        return row.get("regular_rate", 0) === 0 && row.get("overtime_rate", 0) === 0 && row.get("vacation_rate", 0) === 0;
    }
};

export class TimecardProcessing extends AutogenLayoutTimecardProcessing {
    // Server-equivalent constants and helpers for hourly rate calculations
    private static readonly TOTAL_HRS_PER_YEAR = 2080.0;

    private static roundHalfUp(value: number, scale: number): number {
        if (!isFinite(value)) return 0;
        const factor = Math.pow(10, scale);
        // For positive numbers, Math.round implements HALF_UP behavior
        return Math.round(value * factor) / factor;
    }

    public static getNumPayPeriods(frequency: string): number {
        if (!frequency) throw new Error("Unknown payment frequency [" + frequency + "]");
        switch (frequency) {
            case "W":
                return 52;
            case "B":
                return 26;
            case "S":
                return 24; // Semi-monthly
            case "M":
                return 12;
            case "Q":
                return 4;
            case "A":
                return 1;
        }
        throw new Error("Unknown payment frequency [" + frequency + "]");
    }

    public static calculateHoursPerPayPeriod(paymentFrequency: string): number {
        const periods = TimecardProcessing.getNumPayPeriods(paymentFrequency);
        const hours = TimecardProcessing.TOTAL_HRS_PER_YEAR / periods;
        return TimecardProcessing.roundHalfUp(hours, 4);
    }

    /**
     * Calculate hourly rate from a payee row field.
     * Mirrors server TimecardUtil.calculateHourlyRate/calcMorePreciseHourlyRate logic.
     * @param payeeRow Source payee ModelRow containing rate fields and pay frequency
     * @param fieldName Field on payeeRow that holds the base rate (e.g., "regular_rate", "overtime_rate", "vacation_pay_rate")
     * @param payMethod Optional pay method override; if not provided, tries payeeRow.get("pay_method")
     */
    public static calculateHourlyRateFromPayee(payeeRow: ModelRow, fieldName: string, payMethod?: string): number {
        if (!payeeRow) return 0;
        const method = (payMethod ?? payeeRow.get("pay_method")) as string;
        const baseRate = Number.parseFloat(payeeRow.get(fieldName, 0));
        if (!isFinite(baseRate) || baseRate === 0) return 0;

        let hourlyRate: number;
        if (method === "S") { // Salary
            const freq = (payeeRow.get("pay_frequency") || payeeRow.get("payment_frequency")) as string;
            const hoursPerPeriod = TimecardProcessing.calculateHoursPerPayPeriod(freq);
            if (hoursPerPeriod === 0) return 0;
            // Server uses scale 7 for intermediate division, then rounds to 4. We'll compute precise and round to 4.
            hourlyRate = baseRate / hoursPerPeriod;
        } else {
            hourlyRate = baseRate;
        }
        return TimecardProcessing.roundHalfUp(hourlyRate, 4);
    }

    private filterPanel: FilterPanel;
    private payeeDataLookup: SettlementPayeeDataLookup;

    onLoad(): void | Promise<void> {
        this.filterPanel = new FilterPanel([NEEDS_REVIEW, ENTERED_HOURS, MISSING_RATES], this.tableTimecards, this.panelTaskFilters);
        this.mainDataSource.orderBy = [
            { field: "payee_name", sort: "asc" },
            { field: "payee_process_status", sort: "desc" }
        ];
        this.tableTotals["_tbody"].style.overflowX = "hidden";
        this.tableTimecards.addResizeListener((_event: ResizeEvent) => {
            this.syncTotalsColumnWidths()
        });
        this.tableTimecards.addFilterChangedListener((event: TableFilterChangedEvent) => {
            this.setTotalsRow();
        });
        this.tableTimecards["_tbody"].addEventListener("scroll", (event: Event) => {
            const target = event.target as HTMLElement;
            this.tableTotals["_tbody"].scrollLeft = target.scrollLeft;
        });
    }

    private syncTotalsColumnWidths(): void {
        try {
            const main = this.tableTimecards;
            const totals = this.tableTotals;
            if (!main || !totals) return;
            const mainCols = main.columns;
            const totalsCols = totals.columns;
            if (!mainCols || !totalsCols) return;

            const count = Math.min(mainCols.length, totalsCols.length);
            for (let i = 0; i < count; i++) {
                const mainHeading = mainCols[i]?.headingCell as any;
                const totalsHeading = totalsCols[i]?.headingCell as any;
                if (!mainHeading?._element || !totalsHeading) continue;

                const width = DOMUtil.getElementWidth(mainHeading._element);
                if (isFinite(width) && width > 0) {
                    // Lock the totals column to the same width as the main table
                    totalsHeading.fixedWidth = width;
                    // Also apply to the body cells of the totals table (it usually has a single row)
                    for (const row of totals.allRows ?? []) {
                        if (row?.cells?.length > i) {
                            const cell: any = row.cells[i];
                            if (cell) cell.fixedWidth = width;
                        }
                    }
                }
            }
        } catch (_e) {
            // no-op: syncing widths is a best-effort operation and should never break the page
        }
    }

    public static showCrud(props: TimecardProcessingProps): void {
        const layout = Layout.getLayout("lme/settlement/TimecardProcessing") as TimecardProcessing;
        const crud = new CrudDecorator({
            layout: layout,
            headerProps: { showClose: true, showExecute: false },
            layoutLoadListeners: () => {
                if (props.dataSource.url === layout.sourceTimecard.url) {
                    layout.replaceMainDatasource(layout.sourceTimecard);
                }
                layout.payeeDataLookup = props.payeeDataLookup;
                if (props.payeeId) {
                    layout.tableTimecards.setFilter(props.payeeId);
                }
                if (getPayrollControlBoolean("addl_tax_override_hours", false) === false) {
                    layout.tableTimecards.removeColumn("columnHeaderAdditionalTaxOverrideHours");
                    layout.tableTotals.removeColumn("columnHeaderAdditionalTaxOverrideHoursTotals");
                }
                layout.mainDataSource.setRowsAndMode(DataSourceMode.UPDATE, props.dataSource.data);
                layout.setTotalsRow();
            }
        });
        crud.slideIn();
    }

    tableTimecardsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        let finalFocusableComponent: Component = null;
        tableRow.forEveryChildComponent((component) => {
            this.setTooltipCallback(component, modelRow);
            this.setHoursFieldsEnabled(component, modelRow);
            if (component instanceof Textbox && !modelRow.isNull("payee_process_status")) {
                component.enabled = false;
            }
            if (component instanceof Textbox) {
                component.addBlurListener((event: BlurEvent) => {
                    this.recalculateHoursAndTotals(tableRow);
                });
                if (component.isFocusable() && component.enabled) {
                    finalFocusableComponent = component;
                }
            }
        });
        if (finalFocusableComponent) {
            finalFocusableComponent.addKeyDownListener((event: KeyEvent) => {
                this.textboxOnKeyDown(event);
            });
        }
        const textboxRegularUnits = tableRow.findComponentById("textboxRegularUnits") as Textbox;
        if (textboxRegularUnits) {
            textboxRegularUnits.addPrintableListener((event: PrintableEvent) => {
                tableRow.forEveryChildComponent((component) => {
                    if (!(component instanceof Textbox) || component.id === "textboxPayMethod") {
                        component.marginTop = event.printable ? 0 : 10;
                    }
                });
            });
        }
    }

    setTooltipCallback(component: Component, modelRow: ModelRow) {
        switch (component.id) {
            case "labelTotalPay":
            case "labelPayeeName": {
                if (this.sourceTimecard.url === this.mainDataSource.url) {
                    CommonTooltips.setTooltipContentLayout(component, modelRow.get("id"), "lme/backoffice/officepayroll/TimecardTotalsQuickInfo");
                } else {
                    CommonTooltips.setTooltipContentLayout(component, modelRow.get("id"), "lme/settlement/TimecardTotalsQuickInfo");
                }
                break;
            }
            case "labelTerminalDescr": {
                CommonTooltips.setTooltipContentLayout(component, modelRow.get("terminal_id"), "lme/settlement/TerminalCodeQuickInfo");
                break;
            }
            case "labelDivisionDescr": {
                CommonTooltips.setTooltipContentLayout(component, modelRow.get("division_id"), "lme/settlement/DivisionCodeQuickInfo");
                break;
            }
            case "labelJobClassCodeDescr": {
                CommonTooltips.setTooltipContentLayout(component, modelRow.get("job_class_code_id"), "lme/settlement/JobClassCodeQuickInfo");
                break;
            }
            default: {
                if (component instanceof Label && component.displayType === DisplayType.STRING) {
                    component.tooltip = modelRow.get(component.field);
                }
            }
        }
    }

    public static recalculateHoursAndTotals(modelRow: ModelRow) {
        // if units are null, set to 0
        modelRow.set("regular_units", Number.parseFloat(modelRow.get("regular_units", 0)));
        modelRow.set("overtime_units", Number.parseFloat(modelRow.get("overtime_units", 0)));
        modelRow.set("vacation_units", Number.parseFloat(modelRow.get("vacation_units", 0)));
        modelRow.set("holiday_units", Number.parseFloat(modelRow.get("holiday_units", 0)));
        modelRow.set("sick_units", Number.parseFloat(modelRow.get("sick_units", 0)));
        let regularHours = Number.parseFloat(modelRow.get("regular_units", 0));
        let overtimeHours = Number.parseFloat(modelRow.get("overtime_units", 0));
        const overtimeCutoffHours = Number.parseFloat(modelRow.get("overtime_cutoff", 0));
        if (overtimeCutoffHours > 0 && regularHours > overtimeCutoffHours) {
            const difference = regularHours - overtimeCutoffHours;
            regularHours = overtimeCutoffHours;
            modelRow.set("regular_units", regularHours);
            overtimeHours = difference;
            modelRow.set("overtime_units", overtimeHours);
        }
        const vacationHours = Number.parseFloat(modelRow.get("vacation_units", 0));
        const holidayHours = Number.parseFloat(modelRow.get("holiday_units", 0));
        const sickHours = Number.parseFloat(modelRow.get("sick_units", 0));
        const totalHours = regularHours + overtimeHours + vacationHours + holidayHours + sickHours;
        modelRow.set("total_hours", totalHours);
        const regularRate = Number.parseFloat(modelRow.get("regular_rate", 0));
        const overtimeRate = Number.parseFloat(modelRow.get("overtime_rate", 0));
        const vacationRate = Number.parseFloat(modelRow.get("vacation_rate", 0));
        const payeeCurrency = modelRow.get("payee_currency");
        const regularTotal = new Currency(regularHours * regularRate);
        regularTotal.setCurrencyCode(payeeCurrency);
        const overtimeTotal = new Currency(overtimeHours * overtimeRate);
        overtimeTotal.setCurrencyCode(payeeCurrency);
        const vacationTotal = new Currency(vacationHours * vacationRate);
        vacationTotal.setCurrencyCode(payeeCurrency);
        const holidayTotal = new Currency(holidayHours * regularRate);
        holidayTotal.setCurrencyCode(payeeCurrency);
        const sickTotal = new Currency(sickHours * regularRate);
        sickTotal.setCurrencyCode(payeeCurrency);
        modelRow.set("regular_total", regularTotal);
        modelRow.set("overtime_total", overtimeTotal);
        modelRow.set("vacation_total", vacationTotal);
        modelRow.set("holiday_total", holidayTotal);
        modelRow.set("sick_total", sickTotal);
        const totalPay = new Currency(regularTotal.amount + overtimeTotal.amount + vacationTotal.amount + holidayTotal.amount + sickTotal.amount);
        totalPay.setCurrencyCode(payeeCurrency);
        modelRow.set("total_pay", totalPay);
    }

    private recalculateHoursAndTotals(tableRow: TableRow) {
        if (tableRow.validateSimple()) {
            const modelRow = tableRow.data as ModelRow;
            TimecardProcessing.recalculateHoursAndTotals(modelRow);
            tableRow.displayComponentValues();
        }
    }

    tableTimecardsBeforeRowSave(event: TableRowBeforeSaveEvent) {
        const tableRow = event.getTableRow();
        if (tableRow.validateSimple()) {
            this.recalculateHoursAndTotals(tableRow);
            this.filterPanel.updateFilterCounts();
            this.setTotalsRow();
        }
    }

    textboxOnKeyDown(event: KeyEvent) {
        if (event.key === Keys.TAB) {
            event.stop();
            const textbox = event.target as Textbox;
            const tableRow = TableRow.getContainingTableRow(textbox);
            const index = this.tableTimecards.rows.findIndex(row => tableRow === row);
            let nextRow: TableRow = null;
            for (let i = index + 1; i < this.tableTimecards.rows.length; i++) {
                if (this.tableRowIsEditable(this.tableTimecards.rows[i])) {
                    nextRow = this.tableTimecards.rows[i];
                    break;
                }
            }
            if (nextRow == undefined) {
                nextRow = this.tableTimecards.rows.find((row) => this.tableRowIsEditable(row)) as TableRow;
            }
            tableRow.saveChanges();
            nextRow.editRow();
            nextRow.scrollIntoView();
            const firstFocusableComponent = nextRow.findComponentsByType(Textbox).find((comp) => comp.enabled);
            if (firstFocusableComponent) {
                firstFocusableComponent.focus();
            }
        }
    }

    private tableRowIsEditable(tableRow: TableRow): boolean {
        const modelRow = tableRow.data as ModelRow;
        const hasProcessStatus = !modelRow.isNull("payee_process_status");
        const hasSomeRates = modelRow.get("regular_rate", 0) > 0 || modelRow.get("overtime_rate", 0) > 0 || modelRow.get("vacation_rate", 0) > 0;
        return !hasProcessStatus && hasSomeRates;
    }

    setHoursFieldsEnabled(component: Component, modelRow: ModelRow) {
        switch (component.id) {
            case "textboxHolidayUnits":
            case "textboxSickUnits":
            case "textboxRegularUnits": {
                component.enabled = modelRow.get("regular_rate", 0) > 0;
                component.tooltip = component.enabled ? null : "Missing rates";
                break;
            }
            case "textboxOvertimeUnits": {
                component.enabled = modelRow.get("overtime_rate", 0) > 0;
                component.tooltip = component.enabled ? null : "Missing rates";
                break;
            }
            case "textboxVacationUnits": {
                component.enabled = modelRow.get("vacation_rate", 0) > 0;
                component.tooltip = component.enabled ? null : "Missing rates";
            }
        }
    }

    async buttonAssignHoursOnClick(event: ClickEvent) {
        const layout = Layout.getLayout("lme/settlement/AssignTimecardHours") as AssignTimecardHours;
        const response = await CommonDialogs.showDialog(layout, { title: "Assign Timecard Hours", okCaption: "Apply" });
        if (response.wasCancelled) {
            return;
        }
        if (layout.checkboxAssignHoursByPayFrequency.checked || !layout.textboxHolidayDefaultHours.isEmpty()) {
            this.buttonAssignHours.busy = true;
            for (const row of this.mainDataSource.data) {
                let shouldPost = false;
                const tableRow = this.tableTimecards.allRows.find((r) => r.data.get("id") === row.get("id")) as TableRow;
                if (!(tableRow && this.tableRowIsEditable(tableRow))) {
                    continue;
                }
                let updatedHolidayHours = false;
                if (!layout.textboxHolidayDefaultHours.isEmpty() && row.get("holiday_units", 0) === 0 && row.get("regular_rate", 0) > 0) {
                    row.set("holiday_units", layout.textboxHolidayDefaultHours.getDataValue());
                    shouldPost = true;
                    updatedHolidayHours = true;
                }

                if (layout.checkboxAssignHoursByPayFrequency.checked && row.get("regular_units", 0) === 0 && row.get("regular_rate", 0) > 0 && ["H", "S"].includes(row.get("pay_method"))) {
                    const payeeData = this.payeeDataLookup.getPayeeData(row.get("id"));
                    if (payeeData) {
                        const payeeRow = payeeData.getPayeeRow();
                        const payFrequency = payeeRow.get("pay_frequency");
                        let defaultHours = 0;
                        switch (payFrequency) {
                            case "W":
                                defaultHours = 40;
                                break;
                            case "B":
                                defaultHours = 80;
                                break;
                            case "S":
                                defaultHours = 86.6667;
                                break;
                            case "M":
                                defaultHours = 173.3333;
                                break;
                            case "Q":
                                defaultHours = 520;
                                break;
                            case "A":
                                defaultHours = 2080;
                                break;
                        }
                        row.set("regular_units", defaultHours);
                        if (updatedHolidayHours) {
                            const regularUnits = row.get("regular_units", 0) - row.get("holiday_units", 0);
                            row.set("regular_units", regularUnits >= 0 ? regularUnits : 0);
                        }
                        shouldPost = true;
                    }
                }

                if (shouldPost) {
                    this.recalculateHoursAndTotals(tableRow);
                    await row.post();
                }
            }
            this.setTotalsRow();
            this.buttonAssignHours.busy = false;
            this.mainDataSource.displayDataInBoundComponents();
        }
    }

    buttonImportOnClick(event: ClickEvent) {
        const dataType = this.mainDataSource.url === this.sourceTimecard.url ? "TC" : "DT";
        Navigation.navigateTo(`lme/general/dataimport/FileImportProfileList?dataType=${dataType}`, { newTab: true });
    }

    setTotalsRow() {
        const totalsRow = new ModelRow("");
        for (const row of this.tableTimecards.rows) {
            const modelRow = row.data as ModelRow;
            totalsRow.set("regular_units", (totalsRow.get("regular_units", 0) + modelRow.get("regular_units", 0)));
            totalsRow.set("overtime_units", (totalsRow.get("overtime_units", 0) + modelRow.get("overtime_units", 0)));
            totalsRow.set("vacation_units", (totalsRow.get("vacation_units", 0) + modelRow.get("vacation_units", 0)));
            totalsRow.set("holiday_units", (totalsRow.get("holiday_units", 0) + modelRow.get("holiday_units", 0)));
            totalsRow.set("addl_tax_override_hours", (totalsRow.get("addl_tax_override_hours", 0) + modelRow.get("addl_tax_override_hours", 0)));
            totalsRow.set("sick_units", (totalsRow.get("sick_units", 0) + modelRow.get("sick_units", 0)));
            totalsRow.set("total_hours", (totalsRow.get("total_hours", 0) + modelRow.get("total_hours", 0)));
            totalsRow.set("total_pay", new Currency(totalsRow.getCurrency("total_pay", new Currency(0)).amount + modelRow.getCurrency("total_pay", new Currency(0)).amount));
        }
        this.tableTotals.displayComponentData(null, [totalsRow], 0);
        // After rendering the totals row, sync column widths to match the main table
        this.syncTotalsColumnWidths();
    }
}
